import { Controller, useFormContext } from "react-hook-form";

import { Accordion } from "ui/accordion";
import { Box } from "ui/box";
import { NumberField } from "ui/number-field";
import { AlignmentField } from "containers/admin/clients/client/theme-configuration-page/configuration/email-configuration/components/alignment-field";
import { FormSelect } from "containers/admin/clients/client/theme-configuration-page/configuration/email-configuration/components/form-select";
import { PaddingField } from "containers/admin/clients/client/theme-configuration-page/configuration/email-configuration/components/padding-field";
import { ThemeColorSelect } from "containers/admin/clients/client/theme-configuration-page/configuration/email-configuration/components/theme-color-select";

import { toFixedButtonWidth } from "containers/admin/clients/client/theme-configuration-page/configuration/email-configuration/email-configuration.helpers";

import type {
  EmailConfigurationFormValues,
  ThemeColorOption,
} from "containers/admin/clients/client/theme-configuration-page/configuration/email-configuration/email-configuration.types";

import {
  FIELD_ROW_SX,
  FIELDS_SX,
  FIXED_WIDTH_SX,
  GROW_SX,
} from "containers/admin/clients/client/theme-configuration-page/configuration/email-configuration/email-configuration.styles";
import {
  BORDER_RADIUS_OPTIONS,
  BORDER_STYLE_OPTIONS,
  BUTTON_WIDTH_MODES,
  FIXED_BUTTON_WIDTH_STEP,
  MAX_FIXED_BUTTON_WIDTH,
  MIN_FIXED_BUTTON_WIDTH,
} from "containers/admin/clients/client/theme-configuration-page/configuration/email-configuration/email-configuration.constants";

import Icons from "assets";

type ButtonSectionProps = {
  colorOptions: ThemeColorOption[];
  disabled: boolean;
};

const BUTTON_ALIGNMENT_OPTIONS = [
  { label: <Icons.ButtonAlignLeft role="img" aria-label="Left" />, value: "left" },
  { label: <Icons.ButtonAlignCenter role="img" aria-label="Center" />, value: "center" },
  { label: <Icons.ButtonAlignRight role="img" aria-label="Right" />, value: "right" },
];

export const ButtonSection = ({ colorOptions, disabled }: ButtonSectionProps) => {
  const { control, getValues, watch } = useFormContext<EmailConfigurationFormValues>();
  const isFixedWidth = watch("button.widthMode") === "fixed";
  const hasBorder = watch("button.border.style") !== "none";

  return (
    <Accordion title="Parameters" defaultExpanded>
      <Box sx={FIELDS_SX}>
        <PaddingField name="button.padding" label="Padding (px)" disabled={disabled} />
        <Box sx={FIELD_ROW_SX}>
          <Box sx={GROW_SX}>
            <FormSelect name="button.widthMode" label="Width" options={BUTTON_WIDTH_MODES} disabled={disabled} />
          </Box>
          {isFixedWidth && (
            <Controller
              name="button.fixedWidth"
              control={control}
              render={({ field }) => (
                <NumberField
                  name={field.name}
                  value={Number(field.value) || 0}
                  onValueChange={(value) => field.onChange(String(value ?? 0))}
                  onBlur={() => {
                    field.onChange(toFixedButtonWidth(getValues("button.fixedWidth")));
                    field.onBlur();
                  }}
                  min={MIN_FIXED_BUTTON_WIDTH}
                  max={MAX_FIXED_BUTTON_WIDTH}
                  step={FIXED_BUTTON_WIDTH_STEP}
                  suffix="%"
                  disabled={disabled}
                  sx={FIXED_WIDTH_SX}
                  slotProps={{ input: { "aria-label": "Fixed width (%)" } }}
                />
              )}
            />
          )}
        </Box>
        <AlignmentField name="button.alignment" options={BUTTON_ALIGNMENT_OPTIONS} disabled={disabled} />
        <ThemeColorSelect
          name="button.backgroundColor"
          label="Background color"
          options={colorOptions}
          hasNone
          disabled={disabled}
        />
        <FormSelect name="button.border.radius" label="Border radius" options={BORDER_RADIUS_OPTIONS} disabled={disabled} />
        <FormSelect name="button.border.style" label="Border style" options={BORDER_STYLE_OPTIONS} disabled={disabled} />
        {hasBorder && (
          <>
            <ThemeColorSelect
              name="button.border.color"
              label="Border color"
              options={colorOptions}
              hasNone
              disabled={disabled}
            />
            <Controller
              name="button.border.width"
              control={control}
              render={({ field }) => (
                <NumberField
                  name={field.name}
                  label="Border width (px)"
                  value={Number(field.value) || 0}
                  onValueChange={(value) => field.onChange(String(value ?? 0))}
                  min={0}
                  disabled={disabled}
                />
              )}
            />
          </>
        )}
      </Box>
    </Accordion>
  );
};
